<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, watch } from "vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";
import { say } from "../say";
import type { WidgetProps } from "./contract";

/**
 * A signature: a canvas, rasterised to PNG and stored as a FILE (§8).
 *
 * The whole point of this widget is that nothing about it is special. There is
 * no signature table, no signature endpoint and no signature column type — it is
 * `field_type='signature'`, storage reused from `file`, and a capture surface.
 * That is the pattern for every rich input that comes after it (ADR-F5).
 *
 * Who signed and when are NOT embedded here. They come from the record's own
 * audit columns, because a timestamp a widget wrote is a timestamp a widget can
 * be wrong about.
 *
 * ── ASSINOU, TRAVOU (§2.6 #12) ────────────────────────────────────────────
 *
 * `config.locks_record` é lido pelo SERVIDOR (`api/record-lock.ts`), que recusa
 * qualquer PATCH numa linha já assinada. Aqui isto vira só a ausência do gesto:
 * oferecer "refazer" para o PATCH devolver 409 é exatamente o erro de mostrar um
 * botão que o servidor não deixa apertar.
 */
const props = defineProps<
	WidgetProps & { ownerId?: string; controlId?: string; describedBy?: string }
>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const uploads = inject(UPLOAD_PORT, null);
const canvas = ref<HTMLCanvasElement | null>(null);
const drawing = ref(false);
const dirty = ref(false);
const busy = ref(false);
const failure = ref("");
const stored = ref<string | null>(null);

const fileId = computed(() =>
	props.modelValue === null || props.modelValue === undefined ? "" : String(props.modelValue),
);

/**
 * Assinado NESTA sessão, e por isso ainda refazível.
 *
 * A trava do servidor é sobre o valor ANTERIOR da linha: um registro que está
 * sendo criado ainda não tem anterior, então quem acabou de assinar aqui pode
 * limpar e assinar de novo antes de salvar. Sem esta distinção o widget se
 * fecharia no instante do traço e a pessoa perderia uma assinatura torta.
 */
const signedHere = ref(false);
const locked = computed(
	() => props.field.config.locks_record === true && Boolean(fileId.value) && !signedHere.value,
);

watch(
	fileId,
	async (id) => {
		stored.value =
			id && uploads
				? await uploads.urlFor(id).then(
						(f) => f.url,
						() => null,
					)
				: null;
	},
	{ immediate: true },
);

function context(): CanvasRenderingContext2D | null {
	const element = canvas.value;
	if (!element) return null;
	const ctx = element.getContext("2d");
	if (!ctx) return null;
	ctx.lineWidth = 2;
	ctx.lineCap = "round";
	ctx.lineJoin = "round";
	ctx.strokeStyle = "#111827";
	return ctx;
}

/**
 * Pointer events rather than mouse plus touch.
 *
 * One code path covers a mouse, a finger and a stylus — and a signature is drawn
 * with a finger far more often than with a mouse.
 */
function pointFrom(event: PointerEvent): { x: number; y: number } | null {
	const element = canvas.value;
	if (!element) return null;
	const rect = element.getBoundingClientRect();
	// Scaled: the canvas is laid out by CSS and its backing store is fixed, so a
	// raw offset draws the stroke in the wrong place on any non-1:1 screen.
	return {
		x: ((event.clientX - rect.left) / rect.width) * element.width,
		y: ((event.clientY - rect.top) / rect.height) * element.height,
	};
}

function start(event: PointerEvent): void {
	if (props.disabled) return;
	const ctx = context();
	const point = pointFrom(event);
	if (!ctx || !point) return;
	canvas.value?.setPointerCapture(event.pointerId);
	drawing.value = true;
	dirty.value = true;
	ctx.beginPath();
	ctx.moveTo(point.x, point.y);
}

function move(event: PointerEvent): void {
	if (!drawing.value) return;
	const ctx = context();
	const point = pointFrom(event);
	if (!ctx || !point) return;
	ctx.lineTo(point.x, point.y);
	ctx.stroke();
}

function end(): void {
	drawing.value = false;
}

function clear(): void {
	const ctx = context();
	const element = canvas.value;
	if (ctx && element) ctx.clearRect(0, 0, element.width, element.height);
	dirty.value = false;
	failure.value = "";
}

async function confirm(): Promise<void> {
	const element = canvas.value;
	if (!element || !dirty.value) return;
	if (!uploads) {
		failure.value = say("uploadUnavailable");
		return;
	}

	busy.value = true;
	failure.value = "";
	try {
		const blob = await new Promise<Blob | null>((resolve) => element.toBlob(resolve, "image/png"));
		if (!blob) throw new Error(say("signatureFailed"));

		const file = await uploads.upload({
			blob,
			filename: `${props.field.code}.png`,
			ownerKind: "record",
			ownerId: props.ownerId ?? null,
			purpose: "signature",
		});
		emit("update:modelValue", file.id);
		dirty.value = false;
		signedHere.value = true;
	} catch (e) {
		failure.value = e instanceof Error ? e.message : say("signatureSaveFailed");
	} finally {
		busy.value = false;
	}
}

onBeforeUnmount(end);
</script>

<template>
	<div class="space-y-2">
		<div v-if="fileId && !dirty" class="space-y-2">
			<img
				v-if="stored"
				:src="stored"
				alt="Assinatura"
				class="h-24 w-full rounded-md border border-border bg-surface object-contain"
			/>
			<p v-else class="text-xs text-fg-subtle">assinado</p>
			<p v-if="locked" class="text-xs text-fg-subtle">
				Assinado. Este registro não pode mais ser alterado.
			</p>
			<button
				v-else
				type="button"
				class="text-xs text-fg-subtle underline"
				:disabled="disabled"
				@click="emit('update:modelValue', null)"
			>
				refazer
			</button>
		</div>

		<template v-else>
			<canvas
				:id="controlId"
				ref="canvas"
				width="600"
				height="200"
				class="h-32 w-full touch-none rounded-md border border-dashed border-border bg-surface"
				:aria-label="field.label"
				:aria-invalid="Boolean(error)"
				:aria-describedby="describedBy"
				@pointerdown="start"
				@pointermove="move"
				@pointerup="end"
				@pointercancel="end"
			/>
			<div class="flex items-center gap-3">
				<button
					type="button"
					class="rounded-full border border-border px-3 py-1.5 text-xs text-fg"
					:disabled="disabled || busy || !dirty"
					@click="confirm"
				>
					{{ busy ? "salvando…" : "confirmar assinatura" }}
				</button>
				<button
					type="button"
					class="text-xs text-fg-subtle underline"
					:disabled="disabled || !dirty"
					@click="clear"
				>
					limpar
				</button>
			</div>
			<p v-if="!dirty" class="text-xs text-fg-subtle">assine no quadro acima</p>
		</template>

		<p v-if="failure" class="text-xs text-danger">{{ failure }}</p>
	</div>
</template>
